/**
 * Strategy Intelligence Hub — public/member-facing landing for all
 * Adversyn strategy modules (Phase 12BI). Read-only. No raw JSON.
 * No order buttons. No paper-trading language.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";  // beginner-help Phase 2
import {
  Activity,
  Brain,
  Eye,
  Layers,
  Lightbulb,
  MessageSquare,
  ShieldAlert,
  Sparkles,
} from "lucide-react";
import {
  getAIStackHealth,
  getAIStackSignals,
  type AIStackHealthPayload,
  type AIStackPosture,
  type AIStackSignal,
  type AIStackSignalsPayload,
  type DeploymentStatus,
} from "../lib/aiStackClient";
import {
  getConvictionHealth,
  type ConvictionHealthPayload,
} from "../lib/convictionTrackerClient";
import {
  fetchStrategyIntelligenceSummary,
  type StrategyIntelligenceSummary,
} from "../lib/strategyIntelligenceClient";
import { apiV1Base } from "../lib/apiConfig";
import { TermHelp } from "./BeginnerHelp";
// phase-fallen-edge-ui-v1 / phase-short-intel-ui-v1 — advisory-only Loser
// Intelligence and Short Intelligence panels surfaced into the Hub.
import FallenEdgePanel from "./FallenEdgePanel";
import ShortIntelligencePanel from "./ShortIntelligencePanel";

interface Props {
  onNavigate: (tab: string, path?: string) => void;
  onOpenTutor: (suggestedQuestion?: string) => void;
}

const POSTURE_TONE: Record<AIStackPosture, string> = {
  BUY_SETUP: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  LONG_CANDIDATE: "bg-emerald-500/10 text-emerald-200 border-emerald-500/30",
  SELL_SIGNAL: "bg-red-500/15 text-red-300 border-red-500/40",
  SHORT_CANDIDATE: "bg-red-500/10 text-red-200 border-red-500/30",
  REDUCE_EXPOSURE: "bg-orange-500/10 text-orange-300 border-orange-500/30",
  TAKE_PROFIT: "bg-amber-500/10 text-amber-200 border-amber-500/30",
  HEDGE: "bg-blue-500/10 text-blue-300 border-blue-500/30",
  PAIR_TRADE: "bg-purple-500/10 text-purple-300 border-purple-500/30",
  OPTIONS_DEFINED_RISK: "bg-fuchsia-500/10 text-fuchsia-300 border-fuchsia-500/30",
  WATCH: "bg-slate-500/10 text-slate-300 border-slate-500/30",
  WAIT_FOR_PULLBACK: "bg-yellow-500/10 text-yellow-200 border-yellow-500/30",
  AVOID_CHASE: "bg-orange-500/15 text-orange-300 border-orange-500/40",
  NO_TRADE: "bg-slate-500/10 text-slate-300 border-slate-500/30",
  UNAVAILABLE: "bg-slate-500/10 text-slate-300 border-slate-500/30",
};

const DEPLOY_TONE: Record<DeploymentStatus, string> = {
  VALIDATION_MODE: "bg-blue-500/10 text-blue-300 border-blue-500/30",
  LIVE_ELIGIBLE: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  LIVE_BLOCKED_BY_DEPLOYMENT_GATE: "bg-amber-500/10 text-amber-300 border-amber-500/30",
};

// Phase 12BJ-news-quota: extended freshness tones
const FRESHNESS_TONE: Record<string, string> = {
  LIVE: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  CACHE: "bg-blue-500/10 text-blue-300 border-blue-500/30",
  STALE: "bg-amber-500/10 text-amber-300 border-amber-500/30",
  DEGRADED: "bg-orange-500/10 text-orange-300 border-orange-500/30",
  PROVIDER_LIMITED: "bg-amber-500/10 text-amber-200 border-amber-500/30",
  RATE_LIMITED: "bg-orange-500/15 text-orange-200 border-orange-500/40",
  DAILY_QUOTA_EXHAUSTED: "bg-rose-500/10 text-rose-200 border-rose-500/40",
  LAST_GOOD_CACHE: "bg-blue-500/10 text-blue-200 border-blue-500/30",
  UNAVAILABLE: "bg-slate-500/10 text-slate-300 border-slate-500/30",
  NOT_CONFIGURED: "bg-slate-500/10 text-slate-300 border-slate-500/30",
};

const FRESHNESS_SUFFIX: Record<string, string> = {
  LIVE: "updated within 30 min",
  CACHE: "using cache",
  STALE: "older than 6 h — provider call needed",
  PROVIDER_LIMITED: "using last good cache",
  RATE_LIMITED: "retry scheduled",
  DAILY_QUOTA_EXHAUSTED: "next refresh tomorrow",
  LAST_GOOD_CACHE: "no items this run; cached headlines preserved",
  UNAVAILABLE: "feed not available",
  NOT_CONFIGURED: "no provider configured",
};

function Chip({ tone, label }: { tone: string; label: string }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded border text-[11px] font-semibold ${tone}`}>
      {label.replace(/_/g, " ")}
    </span>
  );
}

const TUTOR_QUESTIONS: string[] = [
  // Phase 12BK — Intelligence Hub chip leftmost.
  "What is the Intelligence Hub?",
  "What is the AI Stack Strategy?",
  "Why did Micron get AVOID_CHASE?",
  "What does BUY_SETUP mean?",
  "What does PAIR_TRADE mean?",
  "What is the Conviction Tracker?",
  "Why are there multiple Signal lanes?",
];

const StrategyIntelligenceHub: React.FC<Props> = ({ onNavigate, onOpenTutor }) => {
  const [health, setHealth] = useState<AIStackHealthPayload | null>(null);
  const [signals, setSignals] = useState<AIStackSignalsPayload | null>(null);
  const [conv, setConv] = useState<ConvictionHealthPayload | null>(null);
  // PHASE12CG — catalyst summary (advisory-only)
  const [catSummary, setCatSummary] = useState<{
    status: string;
    candidate_count: number;
    high_conviction_count: number;
    council_queue_count: number;
    missed_major_count: number;
    top_items: { symbol: string; company_name: string | null; catalyst_type: string | null; institutional_significance_score: number; conviction_hint: string; ai_stack_tags: string[] }[];
    provider_state: string | null;
    newest_news_published_at: string | null;
  } | null>(null);
  const [summary, setSummary] = useState<StrategyIntelligenceSummary | null>(null);  // Phase 12BJ: summary endpoint primary
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [explainerOpen, setExplainerOpen] = useState(false);  // decision-layer-clarity Phase 2

  const refresh = async () => {
    setLoading(true);
    // Phase 12BJ: try the unified summary endpoint first.
    const summaryEnv = await fetchStrategyIntelligenceSummary();
    if (summaryEnv.ok && summaryEnv.data) {
      setSummary(summaryEnv.data);
      setErr(null);
      setLoading(false);
      return;
    }
    // Graceful fallback to the original parallel calls when summary endpoint
    // is unavailable (older backend or transient error).
    const [h, s, c, catRes] = await Promise.all([
      getAIStackHealth(), getAIStackSignals(), getConvictionHealth(),
      fetch(`${apiV1Base()}/strategy-intelligence/catalyst-summary`, { credentials: 'include', cache: 'no-store' })
        .then(r => r.ok ? r.json() : null).catch(() => null),
    ]);
    if (catRes && catRes.ok && catRes.data) setCatSummary(catRes.data); else setCatSummary(null);
    if (h.ok && h.data) setHealth(h.data);
    if (s.ok && s.data) {
      setSignals(s.data);
      setErr(null);
    } else if (!s.ok) {
      setErr(s.error?.message || "fetch failed");
    }
    if (c.ok && c.data) setConv(c.data);
    setLoading(false);
  };

  useEffect(() => {
    refresh();
    const id = window.setInterval(refresh, 60_000);
    return () => window.clearInterval(id);
  }, []);

  // Phase 12BJ: prefer summary endpoint data when available; fall back to
  // the legacy parallel fetch state.
  const topSignals: AIStackSignal[] = useMemo(() => {
    if (summary?.ai_stack?.top_signals?.length) {
      return summary.ai_stack.top_signals as unknown as AIStackSignal[];
    }
    return (signals?.signals || []).slice(0, 6);
  }, [summary, signals]);

  const memoryStorage = useMemo(() => {
    if (summary?.ai_stack?.memory_storage_spotlight?.length) {
      return summary.ai_stack.memory_storage_spotlight as unknown as AIStackSignal[];
    }
    return (signals?.signals || []).filter(s => s.primary_layer === "memory_storage").slice(0, 3);
  }, [summary, signals]);

  const catalystState = ((summary?.ai_stack?.catalyst_provider_state || health?.catalyst_provider_state || health?.source_health?.catalyst_provider_state) || "UNAVAILABLE").toUpperCase();

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="strategy-intelligence-hub">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold flex items-center gap-2">
            <Sparkles size={22} className="text-emerald-300" /> Strategy Intelligence Hub
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            Adversyn's real-money strategy intelligence — across AI Stack Strategy, The Signal, the Conviction Tracker, the AI Council, and Market Pulse.
          </p>
        </div>
        <button
          onClick={refresh}
          className="px-3 py-1.5 rounded border border-slate-700 text-xs hover:bg-slate-800/60"
        >
          Refresh
        </button>
      </div>

      <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 flex items-start gap-3" data-testid="hub-disclaimer">
        <ShieldAlert size={18} className="text-amber-300 shrink-0 mt-0.5" />
        <div className="text-xs text-amber-100/90">
          These are strategy <strong>postures</strong>, not orders. Live execution is gated by the deployment, risk, liquidity, and account systems. Adversyn never enables live trading on its own.
        </div>
      </div>

      <div
        data-testid="hub-glossary-entrypoint"
        className="flex flex-wrap items-center gap-3 rounded-lg border border-emerald-500/30 bg-emerald-500/5 px-3 py-2 text-[12px] text-slate-200"
      >
        <span className="font-bold uppercase tracking-widest text-[10px] text-emerald-300">New here?</span>
        <span>Plain-English definitions for the terms used across this page:</span>
        <Link
          to="/glossary"
          data-testid="hub-glossary-link"
          className="rounded-md border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-[11px] font-black uppercase tracking-widest text-emerald-200 hover:bg-emerald-500/20"
        >
          Open Trading Terms Glossary
        </Link>
      </div>

      {/* decision-layer-clarity Phase 2 — beginner hero/explainer (collapsible) */}
      <section
        data-testid="hub-decision-layer-explainer"
        className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3"
      >
        <button
          type="button"
          data-testid="hub-decision-layer-explainer-toggle"
          onClick={() => setExplainerOpen(v => !v)}
          aria-expanded={explainerOpen}
          className="flex w-full items-center justify-between gap-3 text-left"
        >
          <span className="flex items-center gap-2 text-sm font-bold text-emerald-200">
            <Lightbulb size={16} className="text-emerald-300" />
            Research vs Recommendations — read this first
          </span>
          <span className="text-[11px] font-black uppercase tracking-widest text-emerald-300">
            {explainerOpen ? "Hide" : "Show"}
          </span>
        </button>

        {explainerOpen ? (
          <div className="mt-3 space-y-3 text-[12px] leading-relaxed text-slate-200">
            <div>
              <div className="text-[11px] font-black uppercase tracking-widest text-emerald-300">What this page is</div>
              <p className="mt-1 text-slate-200">
                This page helps explain where Adversyn is paying attention. It does not automatically tell you to buy or sell.
                It organizes market themes, sectors, and strategy ideas so the Decision Layer can later decide whether
                anything is actionable for a specific user profile or paper account.
              </p>
            </div>

            <div>
              <div className="text-[11px] font-black uppercase tracking-widest text-emerald-300">What "strategy posture" means</div>
              <p className="mt-1 text-slate-200">
                A strategy posture is Adversyn's current stance toward a market area. It tells you whether we are avoiding it,
                watching it, preparing for a possible opportunity, or treating it as a higher-conviction candidate. It is not
                automatically a buy or sell instruction.
              </p>
              <div className="mt-2"><TermHelp term="Strategy Posture" /></div>
            </div>

            <div>
              <div className="text-[11px] font-black uppercase tracking-widest text-emerald-300">What "watch" means</div>
              <p className="mt-1 text-slate-200">
                Watch means Adversyn is monitoring the area. It does not mean buy.
              </p>
              <div className="mt-2"><TermHelp term="Watch" /></div>
            </div>

            <div>
              <div className="text-[11px] font-black uppercase tracking-widest text-emerald-300">What this page does NOT do</div>
              <p className="mt-1 text-slate-200">
                It does NOT place trades. It does NOT issue direct buy/sell instructions. Direct recommendations belong in the
                Decision Layer.
              </p>
            </div>

            <div>
              <div className="text-[11px] font-black uppercase tracking-widest text-emerald-300">Key terms</div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                <TermHelp term="Strategy Posture" />
                <TermHelp term="AI Stack Strategy" />
                <TermHelp term="Conviction Tracker" />
                <TermHelp term="AI Council" />
                <TermHelp term="Market Pulse" />
                <TermHelp term="Watch" />
                <TermHelp term="Candidate" />
                <TermHelp term="Decision Layer" />
              </div>
            </div>
          </div>
        ) : (
          <p className="mt-2 text-[11px] text-slate-300">
            Plain-English explainer: these are research postures, not orders. Direct buy/sell recommendations belong in the
            Decision Layer.
          </p>
        )}
      </section>

      {err ? (
        <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3 text-xs text-red-200">{err}</div>
      ) : null}

      {/* AI Stack Strategy */}
      <section className="rounded-lg border border-slate-700/60 bg-slate-900/40 p-4" data-testid="hub-ai-stack">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <Layers size={18} className="text-emerald-300" />
            <h2 className="text-sm font-semibold uppercase tracking-wider">AI Stack Strategy</h2>
          </div>
          <div className="flex flex-col items-end gap-0.5">
            <Chip tone={FRESHNESS_TONE[catalystState] || FRESHNESS_TONE.UNAVAILABLE} label={`Catalyst: ${catalystState}`} />
            <span className="text-[10px] text-slate-400" data-testid="catalyst-state-suffix">{FRESHNESS_SUFFIX[catalystState] || ""}</span>
          </div>
        </div>
        <p className="text-xs text-slate-400 mt-2">
          Real-money strategy intelligence across the AI infrastructure value chain. Memory &amp; Storage, Compute &amp; Accelerators, Networking, Data Centers, Power &amp; Grid, Cloud Hyperscalers, AI Software, and AI-themed ETFs.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3 text-xs">
          <div className="rounded border border-slate-700 p-2">
            <div className="text-slate-400">Universe</div>
            <div>{signals?.signal_count ?? 0} symbols</div>
          </div>
          <div className="rounded border border-slate-700 p-2">
            <div className="text-slate-400">Memory / Storage</div>
            <div>{health?.memory_storage_attestation?.mu_present ? "✓ MU + WDC + STX" : "—"}</div>
          </div>
          <div className="rounded border border-slate-700 p-2">
            <div className="text-slate-400">Top postures</div>
            <div className="truncate">
              {Object.entries(signals?.top_postures || {})
                .filter(([, v]) => (v as number) > 0)
                .slice(0, 3)
                .map(([k, v]) => `${k.replace(/_/g, " ")}:${v}`)
                .join(", ") || "—"}
            </div>
          </div>
          <div className="rounded border border-slate-700 p-2">
            <div className="text-slate-400">Catalyst source</div>
            <div className="truncate">{health?.catalyst_source || health?.source_health?.catalyst_source || "—"}</div>
          </div>
        </div>
        <div className="mt-3 space-y-2">
          {loading && !signals ? (
            <p className="text-xs text-slate-400">loading…</p>
          ) : topSignals.length === 0 ? (
            <p className="text-xs text-slate-400">No AI Stack signals available.</p>
          ) : (
            topSignals.map(s => (
              <div key={s.symbol} className="flex items-center justify-between gap-2 text-xs border-t border-slate-800 pt-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="font-mono font-semibold">{s.symbol}</span>
                  <span className="text-slate-400 truncate">{s.primary_layer.replace(/_/g, " ")}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-slate-400">{s.ai_stack_score}</span>
                  <Chip tone={POSTURE_TONE[s.posture]} label={s.posture} />
                  <Chip tone={DEPLOY_TONE[s.deployment_status]} label={s.deployment_status} />
                </div>
              </div>
            ))
          )}
        </div>

        {memoryStorage.length > 0 ? (
          <div className="mt-4 border-t border-slate-800 pt-3">
            <div className="text-xs uppercase tracking-wider text-slate-400 mb-2">Memory &amp; Storage Spotlight</div>
            <ul className="text-xs space-y-1">
              {memoryStorage.map(s => (
                <li key={`m-${s.symbol}`} className="flex items-center justify-between">
                  <span className="font-mono">{s.symbol}</span>
                  <span className="flex items-center gap-2">
                    <span className="text-slate-400">{s.change_pct !== null ? `${s.change_pct >= 0 ? "+" : ""}${s.change_pct.toFixed(2)}%` : "—"}</span>
                    <Chip tone={POSTURE_TONE[s.posture]} label={s.posture} />
                  </span>
                </li>
              ))}
            </ul>
            <p className="text-[11px] text-slate-500 mt-2">
              MU is real-money strategy AVOID_CHASE when its single-day move is extreme — chase risk dominates the bullish catalyst. Ask the Tutor for the explanation.
            </p>
          </div>
        ) : null}
      </section>

      {/* The Signal — Multi-Lane Radar (formerly Matrix) */}
      <section className="rounded-lg border border-slate-700/60 bg-slate-900/40 p-4" data-testid="hub-signal">
        <div className="flex items-center gap-2">
          <Lightbulb size={18} className="text-yellow-300" />
          <h2 className="text-sm font-semibold uppercase tracking-wider">The Signal — Multi-Lane Radar</h2>
        </div>
        <p className="text-xs text-slate-400 mt-2">
          Premarket-into-open strategy radar across <strong>five lanes</strong>: small-cap runners, large-cap momentum, mega-cap / Mag7, ETF / index movers, and broad-market watch. Lanes are scored separately so micro-caps don't get mixed with mega-caps.
        </p>
        <div className="mt-3 flex gap-2 flex-wrap">
          {["small_cap_runners", "large_cap_momentum", "mega_cap_mag7", "etf_index_movers", "broad_market_watch"].map(l => (
            <Chip key={l} tone="bg-slate-800/60 text-slate-300 border-slate-700" label={l} />
          ))}
        </div>
        <button
          onClick={() => onNavigate("signal", "/signal")}
          className="mt-3 px-3 py-1.5 rounded bg-yellow-500/10 border border-yellow-500/30 text-yellow-200 text-xs hover:bg-yellow-500/20"
          data-testid="hub-signal-cta"
        >
          Open The Signal →
        </button>
      </section>

      {/* Institutional Catalysts (Phase 12CG) */}
      <section className="rounded-lg border border-slate-700/60 bg-slate-900/40 p-4" data-testid="hub-institutional-catalysts">
        <div className="flex items-center gap-2">
          <Sparkles size={18} className="text-amber-300" />
          <h2 className="text-sm font-semibold uppercase tracking-wider">Institutional Catalysts</h2>
          <Chip
            tone={
              catSummary?.provider_state === "LIVE"
                ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30"
                : "bg-slate-700/40 text-slate-300 border-slate-600/50"
            }
            label={catSummary?.provider_state || "UNAVAILABLE"}
          />
        </div>
        <p className="text-xs text-slate-400 mt-2">
          Forward-looking institutional catalyst layer: partnerships, contracts, raised guidance, analyst momentum, AI infrastructure narratives. Surfaces what is about to matter — not what is already moving. Advisory only; never auto-trades; never changes The Signal&apos;s official hit-rate.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3 text-xs">
          <div className="rounded border border-slate-700 p-2">
            <div className="text-slate-400">Candidates</div>
            <div>{catSummary?.candidate_count ?? 0}</div>
          </div>
          <div className="rounded border border-slate-700 p-2">
            <div className="text-slate-400">High conviction</div>
            <div>{catSummary?.high_conviction_count ?? 0}</div>
          </div>
          <div className="rounded border border-slate-700 p-2">
            <div className="text-slate-400">Council queue</div>
            <div>{catSummary?.council_queue_count ?? 0}</div>
          </div>
          <div className="rounded border border-slate-700 p-2">
            <div className="text-slate-400">Missed major</div>
            <div>{catSummary?.missed_major_count ?? 0}</div>
          </div>
        </div>
        {catSummary && catSummary.top_items && catSummary.top_items.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {catSummary.top_items.slice(0, 5).map(it => (
              <span
                key={it.symbol}
                className="inline-flex items-center px-2 py-0.5 rounded border border-slate-600/60 bg-slate-800/40 text-xs"
                title={`${it.catalyst_type || ""}  score ${it.institutional_significance_score}`}
              >
                <span className="font-mono mr-1">{it.symbol}</span>
                <span className="text-slate-400">{it.conviction_hint.replace(/_/g, " ").toLowerCase()}</span>
              </span>
            ))}
          </div>
        )}
        <button
          onClick={() => onNavigate("matrix", "/signal")}
          className="mt-3 px-3 py-1.5 rounded bg-amber-500/10 border border-amber-500/30 text-amber-200 text-xs hover:bg-amber-500/20"
          data-testid="hub-catalyst-cta"
        >
          Open The Signal · Catalyst Watch →
        </button>
        <p className="text-[11px] text-slate-500 mt-3">
          Catalyst evidence is news-driven and can be noisy. Always verify with primary sources (10-Q, IR pages) before acting. The Adversyn brain never converts a catalyst watch into a trade automatically.
        </p>
      </section>

      {/* Conviction Tracker */}
      <section className="rounded-lg border border-slate-700/60 bg-slate-900/40 p-4" data-testid="hub-conviction">
        <div className="flex items-center gap-2">
          <Eye size={18} className="text-indigo-300" />
          <h2 className="text-sm font-semibold uppercase tracking-wider">Conviction Tracker</h2>
        </div>
        <p className="text-xs text-slate-400 mt-2">
          Smart-money / insider disclosure intelligence. Live SEC Form 4 ingest plus a 13F scaffold and 13D/G classifier. Political / OGE feeds default to NOT_CONFIGURED — no fabricated data.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3 text-xs">
          <div className="rounded border border-slate-700 p-2">
            <div className="text-slate-400">Provider</div>
            <div>{conv?.sec_ingest?.provider_state || "—"}</div>
          </div>
          <div className="rounded border border-slate-700 p-2">
            <div className="text-slate-400">Disclosures</div>
            <div>{conv?.disclosure_count ?? 0}</div>
          </div>
          <div className="rounded border border-slate-700 p-2">
            <div className="text-slate-400">Signals</div>
            <div>{conv?.signal_count ?? 0}</div>
          </div>
          <div className="rounded border border-slate-700 p-2">
            <div className="text-slate-400">Watchlist</div>
            <div>{conv?.sec_ingest?.watchlist_count ?? 0} symbols</div>
          </div>
        </div>
        <p className="text-[11px] text-slate-500 mt-3">
          Filings are delayed and incomplete. Adversyn never recommends copying a hedge fund's, politician's, or insider's portfolio blindly.
        </p>
      </section>

      {/* phase-fallen-edge-ui-v1 — Loser Intelligence (advisory-only). */}
      <section data-testid="hub-fallen-edge">
        <FallenEdgePanel />
      </section>

      {/* phase-short-intel-ui-v1 — Short Intelligence (advisory-only). */}
      <section data-testid="hub-short-intel">
        <ShortIntelligencePanel />
      </section>

      {/* Council */}
      <section className="rounded-lg border border-slate-700/60 bg-slate-900/40 p-4" data-testid="hub-council">
        <div className="flex items-center gap-2">
          <Brain size={18} className="text-purple-300" />
          <h2 className="text-sm font-semibold uppercase tracking-wider">AI Council</h2>
        </div>
        <p className="text-xs text-slate-400 mt-2">
          Multi-provider symbol deliberation (Claude, OpenAI, Gemini, Perplexity) aggregated into a single advisory view. The Council's <code className="text-slate-200">live_context</code> now also surfaces the AI Stack Strategy and Conviction Tracker context as decision-support metadata — never as an order.
        </p>
        <button
          onClick={() => onNavigate("council", "/council")}
          className="mt-3 px-3 py-1.5 rounded bg-purple-500/10 border border-purple-500/30 text-purple-200 text-xs hover:bg-purple-500/20"
          data-testid="hub-council-cta"
        >
          Open Council Debate →
        </button>
      </section>

      {/* Tutor */}
      <section className="rounded-lg border border-slate-700/60 bg-slate-900/40 p-4" data-testid="hub-tutor">
        <div className="flex items-center gap-2">
          <MessageSquare size={18} className="text-sky-300" />
          <h2 className="text-sm font-semibold uppercase tracking-wider">Ask the AI Tutor</h2>
        </div>
        <p className="text-xs text-slate-400 mt-2">
          Plain-English explanations of every module. The Tutor refuses to recommend trades or override safety gates — that's by design.
        </p>
        <div className="flex flex-wrap gap-2 mt-3">
          {(summary?.tutor?.suggested_questions || TUTOR_QUESTIONS).map(q => (
            <button
              key={q}
              onClick={() => {
                // Phase 12BJ: dispatch CustomEvent so AITutor prefills its input
                // box; keep onOpenTutor callback for backward compatibility.
                try {
                  window.dispatchEvent(new CustomEvent("adversyn:tutor-prefill", { detail: { question: q } }));
                } catch { /* ignore - older browsers */ }
                onOpenTutor(q);
              }}
              className="px-3 py-1.5 rounded bg-sky-500/10 border border-sky-500/30 text-sky-100 text-xs hover:bg-sky-500/20"
              data-testid={`hub-tutor-q-${q.slice(0, 20).replace(/[^A-Za-z0-9]/g, "_")}`}
            >
              {q}
            </button>
          ))}
        </div>
      </section>

      <div className="text-[11px] text-slate-500 text-center pt-2" data-testid="hub-footer">
        <Activity size={12} className="inline mr-1" />
        Strategy signals only. Live execution requires the deployment, risk, liquidity, and account gates.
      </div>
    </div>
  );
};

export default StrategyIntelligenceHub;
